import { derivedContext, derivedContextType } from "@/contexts/context.derived";
import { stateContext, stateContextType } from "@/contexts/context.state";
import { storeContext, storeContextType } from "@/contexts/context.store";
import { CollectController } from "@/controllers/collect-controller";
import { ComputeController } from "@/controllers/compute-controller";
import { EtaRenderController } from "@/controllers/eta-renderer";
import { EventPipelineController } from "@/controllers/event-pipeline-controller";
import { ActionEvent, CollectAction, CollectOption, CollectOptionEvent, CollectUpdateByDelta, EventType, UpdateByDeltaEvent } from "@/events/events";
import { toDSLContext } from "@/renders/context";
import { consume, provide } from "@lit/context";
import { ProductOption } from "@repobit/dex-store";
import { html, LitElement } from "lit";
import { property } from "lit/decorators.js";

type Options = Map<symbol | string, Promise<ProductOption[]>>;
type Actions = Map<symbol | string, NonNullable<CollectAction["action"]>>;
type DeltaUpdate = Map<symbol | string, NonNullable<CollectUpdateByDelta["update"]>>;

if (!window.Promise.withResolvers) {
  window.Promise.withResolvers = function<T>() {
    let resolve, reject;
    const promise = new Promise<T>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return { resolve, reject, promise } as unknown as PromiseWithResolvers<T>;
  }
}

export class StateNode extends LitElement {
  @property({ type: Boolean })
  autoForward = true;
  @property({ type: Boolean, attribute: 'no-collect' })
  noCollect = false;
  @property({ attribute: 'ignore-events' })
  ignoreEvents?: string;
  @property({ attribute: 'store-name' })
  storeName: symbol | string = Symbol("bd-state");

  // Configurable delay for mutation-triggered Eta coalescing (ms).
  //  - Use 0 for next-tick behavior (runs ASAP, more visits)
  //  - Use a higher value (e.g., 50–150) to batch SPA bursts
  @property({ attribute: 'eta-mutation-delay', type: Number })
  etaMutationDelay = 10;
  @property({ attribute: 'eta-mutation-delay-max', type: Number })
  etaMutationDelayMax = 100;

  // If true, ignore events coming from parent context; only react to
  // events originating inside this subtree (DOM-bubbled via listeners).
  @property({ type: Boolean, attribute: 'ignore-events-parent' })
  ignoreEventsParent = false;

  /**
   * all options as seens by this node
   * options + partialOptions
   */
  _options: Options = new Map();

  /**
   * all actions as seens by this node
   */
  _actions: Actions = new Map();

  /**
   * all delta updates as seens by this node
   * changes that affect only devices and subscription
   */
  _deltaUpdates: DeltaUpdate = new Map();

  /**
   * all product changes as seens by this node
   */
  _partialOptions: Actions = new Map();

  /**
   * all product changes as seens by this node
   */
  _partialBundleOptions: Actions = new Map();

  /**
   * Consumes store provided by root node
   */
  @consume({ context: storeContext, subscribe: true  })
  @property({ attribute: false })
  _store?: storeContextType;

  /**
   * Raw event pushed from parent (if any)
   */
  @property({ attribute: false })
  _eventParent?: EventType;

  // User-defined derived context for Eta/DSL
  @consume({ context: derivedContext, subscribe: true })
  @property({ attribute: false })
  _derived?: derivedContextType | null;

  // Effective event used by this node (merged from DOM and/or parent)
  @property({ attribute: false })
  _event?: EventType;

  // Last DOM-bubbled event captured at this node
  @property({ attribute: false })
  _eventDom?: EventType;

  /**
   * Local state
   */
  @provide({ context: stateContext })
  @property({ attribute: false })
  state: stateContextType = {
    price: {
      min: {
        value: Number.MAX_SAFE_INTEGER,
        fmt  : ""
      },
      max: {
        value: Number.MIN_SAFE_INTEGER,
        fmt  : ""
      },
      monthly: {
        min: {
          value: Number.MAX_SAFE_INTEGER,
          fmt  : ""
        },
        max: {
          value: Number.MIN_SAFE_INTEGER,
          fmt  : ""
        }
      }
    },
    discountedPrice: {
      min: {
        value: Number.MAX_SAFE_INTEGER,
        fmt  : ""
      },
      max: {
        value: Number.MIN_SAFE_INTEGER,
        fmt  : ""
      },
      monthly: {
        min: {
          value: Number.MAX_SAFE_INTEGER,
          fmt  : ""
        },
        max: {
          value: Number.MIN_SAFE_INTEGER,
          fmt  : ""
        }
      }
    },
    discount: {
      min: {
        value: Number.MAX_SAFE_INTEGER,
        fmt  : ""
      },
      max: {
        value: Number.MIN_SAFE_INTEGER,
        fmt  : ""
      },
      monthly: {
        min: {
          value: Number.MAX_SAFE_INTEGER,
          fmt  : ""
        },
        max: {
          value: Number.MIN_SAFE_INTEGER,
          fmt  : ""
        }
      },
      percentage: {
        min: {
          value: Number.MAX_SAFE_INTEGER,
          fmt  : ""
        },
        max: {
          value: Number.MIN_SAFE_INTEGER,
          fmt  : ""
        },
        monthly: {
          min: {
            value: Number.MAX_SAFE_INTEGER,
            fmt  : ""
          },
          max: {
            value: Number.MIN_SAFE_INTEGER,
            fmt  : ""
          }
        }
      }
    }
  };

  /**
   * all options computed by this node
   * options * actions
   */
  get computedOptions() {
    return this._compute.value;
  }

  private _compute = new ComputeController(this);
  private _events = new EventPipelineController(this, (e) => this._forwardEvent(e), () => this._notifyParent());
  private _collect = new CollectController(
    this,
    () => this._compute.abort(),
    () => this._compute.run(),
    () => this._notifyParent()
  );
  private _eta = new EtaRenderController(this);

  // Allow subclasses to disable Eta rendering entirely.
  shouldRunEtaStateRender(): boolean { return true; }

  // Allow subclasses to opt out of mutation-triggered Eta runs.
  shouldObserveEtaMutations(): boolean {
    return this.shouldRunEtaStateRender();
  }

  getEtaArgs(): unknown[] {
    return [this.state, this._derived];
  }

  async buildEtaContext(): Promise<object | undefined> {
    return await toDSLContext({ state: this.state, derived: this._derived });
  }

  isStateNodeElement(el: Element): boolean {
    if (el instanceof StateNode) return true;
    const t = el.tagName;
    return t === 'BD-STATE' || t === 'BD-PRODUCT' || t === 'BD-OPTION' || t === 'BD-CONTEXT';
  }

  connectedCallback() {
    super.connectedCallback();
    [ActionEvent, UpdateByDeltaEvent].forEach(e => this.addEventListener(e.eventName, this._eventChange))
  }

  remove(): void {
    // Announce removal to parent before DOM detaches, so it can unregister us
    this._collect.announceRemoval();
    this.dispatchEvent(
      new CollectOptionEvent({ name: this.storeName, options: null })
    );
    super.remove();
  }

  disconnectedCallback() {
    [ActionEvent, UpdateByDeltaEvent].forEach(e => this.removeEventListener(e.eventName, this._eventChange))
    super.disconnectedCallback();
  }

  protected _eventChange(e: EventType) {
    e.stopPropagation();
    // ignore if source matches ignore list
    if (this._isIgnoredSource(e)) {
      return;
    }
    this._eventDom = e;
  }

  protected _forwardEvent(e = this._event ) {
    this._pushEventToChildren(e);
  }

  protected _receiveParentEvent(e?: EventType) {
    if (this.ignoreEventsParent) {
      this._eventParent = undefined;
      return;
    }
    this._eventParent = e;
  }

  protected _pushEventToChildren(e?: EventType) {
    if (!e) return;
    const children = this._collect.getContextualChildren();
    for (const child of children) {
      if (child === this) continue;
      if (child instanceof StateNode) {
        child._receiveParentEvent(e);
      }
    }
  }

  collectOption({ name, options }: CollectOption) {
    this._collect.collectOption({ name, options });
  }

  protected _notifyParent() {
    if (this.noCollect) return;
    this.dispatchEvent(
      new CollectOptionEvent({
        name   : this.storeName,
        options: this._compute.taskComplete as Promise<ProductOption[]>
      })
    );
  }

  protected async getUpdateComplete() {
    const result = await super.getUpdateComplete()
    await this._compute.taskComplete;
    await this._events.forwardComplete;
    await this._events.syncComplete;
    await this._collect.toggleComplete;
    await this._eta.taskComplete;
    // Ensure any debounced/mutation-triggered Eta runs have settled
    await this._eta.waitForIdle();
    await this._collect.waitForChildrenUpdateComplete();
    return result;
  }

  protected isDeviceAndSubscriptionChange(evt: EventType): evt is ActionEvent {
    return evt instanceof ActionEvent
        && Boolean(evt.detail?.devices || evt.detail?.subscription)
        && !evt.detail.bundle;
  }

  protected isBundleToogle(evt: EventType): evt is ActionEvent {
    return evt instanceof ActionEvent
        && Boolean(evt.detail?.id)
        && Boolean(evt.detail.bundle);
  }

  protected isProductChange(evt: EventType): evt is ActionEvent {
    return evt instanceof ActionEvent
        && Boolean(evt.detail?.id)
        && !evt.detail.bundle;
  }

  protected isActionEvent(evt: EventType): evt is ActionEvent {
    return this.isProductChange(evt) || this.isDeviceAndSubscriptionChange(evt);
  }

  protected isDeltaUpdate(evt: EventType): evt is UpdateByDeltaEvent {
    return evt instanceof UpdateByDeltaEvent
  }

  protected _isIgnoredSource(e: EventType): boolean {
    const set = this._getIgnoreSet();
    if (set.size === 0) return false;

    const id = e.detail?.storeId || "";
    return set.has(id);
  }

  private _getIgnoreSet(): Set<string> {
    const raw = this.ignoreEvents;
    if (!raw) return new Set();
    return new Set(
      raw
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    );
  }

  render() {
    return html`<slot></slot>`;
  }
}
